import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Plus,
  Trash2,
  ShoppingCart,
  Send
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import purchaseOrderService from '../../services/purchaseOrderService'
import purchaseRequisitionService from '../../services/purchaseRequisitionService'
import productMasterService from '../../services/productMasterService'
import procurementServiceMasterService from '../../services/procurementServiceMasterService'
import categoryMasterService from '../../services/categoryMasterService'
import supplierMasterService from '../../services/supplierMasterService'
import '../../styles/expenseClaimForm.css'

const PurchaseOrderForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEdit = location.pathname.includes('/edit')
  const isView = location.pathname.includes('/view')
  const orderData = location.state?.orderData

  const [formData, setFormData] = useState({
    purchaseOrderNumber: '',
    purchaseRequisition: '',
    requisitionNumber: '',
    supplier: '',
    supplierName: '',
    supplierCode: '',
    supplierContact: {
      contactPerson: '',
      email: '',
      phone: ''
    },
    supplierAddress: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'India'
    },
    orderDate: new Date().toISOString().split('T')[0],
    expectedDeliveryDate: '',
    deliveryAddress: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'India'
    },
    orderItems: [],
    financialSummary: {
      subtotal: 0,
      totalDiscount: 0,
      totalTaxAmount: 0,
      shippingCharges: 0,
      otherCharges: 0,
      totalAmount: 0,
      currency: 'INR'
    },
    paymentTerms: {
      paymentMethod: 'Net 30',
      paymentTermsDescription: '',
      advancePercentage: 0,
      advanceAmount: 0
    },
    deliveryTerms: {
      deliveryMethod: 'Supplier Delivery',
      incoterms: '',
      deliveryTermsDescription: ''
    },
    project: '',
    projectName: '',
    department: '',
    costCenter: '',
    notes: '',
    internalNotes: ''
  })

  const [isLoadingOrderNumber, setIsLoadingOrderNumber] = useState(true)
  const [suppliers, setSuppliers] = useState([])
  const [requisitions, setRequisitions] = useState([])
  const [products, setProducts] = useState([])
  const [services, setServices] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)

  // Load initial data
  useEffect(() => {
    if (!isEdit && !isView) {
      fetchOrderNumber()
    } else if (orderData) {
      loadOrderData(orderData._id)
    }

    // Load dropdown data
    loadDropdownData()
  }, [])

  const fetchOrderNumber = async () => {
    try {
      setIsLoadingOrderNumber(true)
      const response = await purchaseOrderService.getNextPurchaseOrderNumber()
      if (response.success && response.data) {
        setFormData(prev => ({
          ...prev,
          purchaseOrderNumber: response.data.purchaseOrderNumber || response.data.formattedValue || 'PO0001'
        }))
      }
    } catch (error) {
      console.error('Error fetching order number:', error)
      setFormData(prev => ({ ...prev, purchaseOrderNumber: 'PO0001' }))
    } finally {
      setIsLoadingOrderNumber(false)
    }
  }

  const loadOrderData = async (id) => {
    try {
      setLoading(true)
      const response = await purchaseOrderService.getPurchaseOrderById(id)
      if (response.success && response.data) {
        const order = response.data
        setFormData({
          purchaseOrderNumber: order.purchaseOrderNumber || '',
          purchaseRequisition: order.purchaseRequisition?._id || order.purchaseRequisition || '',
          requisitionNumber: order.requisitionNumber || '',
          supplier: order.supplier?._id || order.supplier || '',
          supplierName: order.supplierName || '',
          supplierCode: order.supplierCode || '',
          supplierContact: order.supplierContact || {
            contactPerson: '',
            email: '',
            phone: ''
          },
          supplierAddress: order.supplierAddress || {
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: 'India'
          },
          orderDate: order.orderDate ? new Date(order.orderDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          expectedDeliveryDate: order.expectedDeliveryDate ? new Date(order.expectedDeliveryDate).toISOString().split('T')[0] : '',
          deliveryAddress: order.deliveryAddress || {
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: 'India'
          },
          orderItems: order.orderItems || [],
          financialSummary: order.financialSummary || {
            subtotal: 0,
            totalDiscount: 0,
            totalTaxAmount: 0,
            shippingCharges: 0,
            otherCharges: 0,
            totalAmount: 0,
            currency: 'INR'
          },
          paymentTerms: order.paymentTerms || {
            paymentMethod: 'Net 30',
            paymentTermsDescription: '',
            advancePercentage: 0,
            advanceAmount: 0
          },
          deliveryTerms: order.deliveryTerms || {
            deliveryMethod: 'Supplier Delivery',
            incoterms: '',
            deliveryTermsDescription: ''
          },
          project: order.project?._id || order.project || '',
          projectName: order.projectName || '',
          department: order.department || '',
          costCenter: order.costCenter || '',
          notes: order.notes || '',
          internalNotes: order.internalNotes || ''
        })
      }
    } catch (error) {
      console.error('Error loading order data:', error)
      toast.error('Failed to load order data')
    } finally {
      setLoading(false)
      setIsLoadingOrderNumber(false)
    }
  }

  const loadDropdownData = async () => {
    try {
      const [suppliersRes, requisitionsRes, productsRes, servicesRes, categoriesRes] = await Promise.all([
        supplierMasterService.getAllSuppliers({ status: 'Active', limit: 1000 }),
        purchaseRequisitionService.getAllPurchaseRequisitions({ status: 'Approved', limit: 1000 }),
        productMasterService.getAllProducts({ status: 'Active', limit: 1000 }),
        procurementServiceMasterService.getAllProcurementServices({ status: 'Active', limit: 1000 }),
        categoryMasterService.getAllCategories({ status: 'Active', limit: 1000 })
      ])

      if (suppliersRes.success) setSuppliers(suppliersRes.data || [])
      if (requisitionsRes.success) setRequisitions(requisitionsRes.data || [])
      if (productsRes.success) setProducts(productsRes.data || [])
      if (servicesRes.success) setServices(servicesRes.data || [])
      if (categoriesRes.success) setCategories(categoriesRes.data || [])
    } catch (error) {
      console.error('Error loading dropdown data:', error)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name.startsWith('supplierContact.')) {
      const contactField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        supplierContact: {
          ...prev.supplierContact,
          [contactField]: value
        }
      }))
    } else if (name.startsWith('supplierAddress.')) {
      const addressField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        supplierAddress: {
          ...prev.supplierAddress,
          [addressField]: value
        }
      }))
    } else if (name.startsWith('deliveryAddress.')) {
      const addressField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        deliveryAddress: {
          ...prev.deliveryAddress,
          [addressField]: value
        }
      }))
    } else if (name.startsWith('financialSummary.')) {
      const financialField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        financialSummary: {
          ...prev.financialSummary,
          [financialField]: type === 'number' ? parseFloat(value) || 0 : value
        }
      }))
    } else if (name.startsWith('paymentTerms.')) {
      const paymentField = name.split('.')[1]
      setFormData(prev => {
        const newPaymentTerms = {
          ...prev.paymentTerms,
          [paymentField]: type === 'number' ? parseFloat(value) || 0 : value
        }
        // Calculate advance amount if percentage changes
        if (paymentField === 'advancePercentage') {
          newPaymentTerms.advanceAmount = (prev.financialSummary.totalAmount * parseFloat(value) || 0) / 100
        }
        return {
          ...prev,
          paymentTerms: newPaymentTerms
        }
      })
    } else if (name.startsWith('deliveryTerms.')) {
      const deliveryField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        deliveryTerms: {
          ...prev.deliveryTerms,
          [deliveryField]: value
        }
      }))
    } else if (name === 'supplier') {
      const selectedSupplier = suppliers.find(s => s._id === value)
      if (selectedSupplier) {
        setFormData(prev => ({
          ...prev,
          supplier: value,
          supplierName: selectedSupplier.supplierName || '',
          supplierCode: selectedSupplier.supplierCode || '',
          supplierContact: {
            contactPerson: selectedSupplier.primaryContact?.contactPerson || '',
            email: selectedSupplier.primaryContact?.email || '',
            phone: selectedSupplier.primaryContact?.phone || ''
          },
          supplierAddress: selectedSupplier.billingAddress || {
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: 'India'
          }
        }))
      }
    } else if (name === 'purchaseRequisition') {
      const selectedRequisition = requisitions.find(r => r._id === value)
      if (selectedRequisition) {
        setFormData(prev => ({
          ...prev,
          purchaseRequisition: value,
          requisitionNumber: selectedRequisition.requisitionNumber || '',
          orderItems: selectedRequisition.requisitionItems?.map((item, idx) => ({
            itemNumber: idx + 1,
            itemType: item.itemType || 'Product',
            product: item.product?._id || item.product || '',
            productName: item.productName || '',
            productCode: item.productCode || '',
            service: item.service?._id || item.service || '',
            serviceName: item.serviceName || '',
            serviceCode: item.serviceCode || '',
            category: item.category?._id || item.category || '',
            categoryName: item.categoryName || '',
            description: item.description || '',
            quantity: item.quantity || 1,
            unitOfMeasurement: item.unitOfMeasurement || 'Unit',
            unitPrice: item.estimatedUnitPrice || 0,
            discount: 0,
            discountAmount: 0,
            taxRate: 0,
            taxAmount: 0,
            totalAmount: 0,
            currency: item.currency || 'INR',
            expectedDeliveryDate: item.requiredDate || ''
          })) || []
        }))
        calculateTotals()
      }
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.orderItems]
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: field === 'quantity' || field === 'unitPrice' || field === 'discount' || field === 'taxRate'
        ? parseFloat(value) || 0
        : value
    }

    // Calculate item totals
    const quantity = updatedItems[index].quantity || 0
    const unitPrice = updatedItems[index].unitPrice || 0
    const discount = updatedItems[index].discount || 0
    const taxRate = updatedItems[index].taxRate || 0

    const itemSubtotal = quantity * unitPrice
    const discountAmount = itemSubtotal * (discount / 100)
    const afterDiscount = itemSubtotal - discountAmount
    const taxAmount = afterDiscount * (taxRate / 100)

    updatedItems[index].discountAmount = discountAmount
    updatedItems[index].taxAmount = taxAmount
    updatedItems[index].totalAmount = afterDiscount + taxAmount

    // Update names when IDs are selected
    if (field === 'product') {
      const selectedProduct = products.find(p => p._id === value)
      if (selectedProduct) {
        updatedItems[index].productName = selectedProduct.productName || ''
        updatedItems[index].productCode = selectedProduct.productCode || ''
        updatedItems[index].unitOfMeasurement = selectedProduct.unitOfMeasurement || 'Unit'
      }
    }

    if (field === 'service') {
      const selectedService = services.find(s => s._id === value)
      if (selectedService) {
        updatedItems[index].serviceName = selectedService.serviceName || ''
        updatedItems[index].serviceCode = selectedService.serviceCode || ''
        updatedItems[index].unitOfMeasurement = selectedService.unitOfMeasurement || 'Hour'
      }
    }

    if (field === 'category') {
      const selectedCategory = categories.find(c => c._id === value)
      if (selectedCategory) {
        updatedItems[index].categoryName = selectedCategory.categoryName || ''
      }
    }

    setFormData(prev => ({
      ...prev,
      orderItems: updatedItems
    }))
  }

  const calculateTotals = () => {
    const subtotal = formData.orderItems.reduce((sum, item) => 
      sum + ((item.unitPrice || 0) * (item.quantity || 0)), 0)
    const totalDiscount = formData.orderItems.reduce((sum, item) => 
      sum + (item.discountAmount || 0), 0)
    const totalTaxAmount = formData.orderItems.reduce((sum, item) => 
      sum + (item.taxAmount || 0), 0)
    const totalAmount = subtotal - totalDiscount + totalTaxAmount + 
      (formData.financialSummary.shippingCharges || 0) + 
      (formData.financialSummary.otherCharges || 0)

    setFormData(prev => ({
      ...prev,
      financialSummary: {
        ...prev.financialSummary,
        subtotal,
        totalDiscount,
        totalTaxAmount,
        totalAmount
      },
      paymentTerms: {
        ...prev.paymentTerms,
        advanceAmount: (totalAmount * (prev.paymentTerms.advancePercentage || 0)) / 100
      }
    }))

    return {
      subtotal,
      totalDiscount,
      totalTaxAmount,
      totalAmount
    }
  }

  const addOrderItem = () => {
    setFormData(prev => ({
      ...prev,
      orderItems: [
        ...prev.orderItems,
        {
          itemNumber: prev.orderItems.length + 1,
          itemType: 'Product',
          product: '',
          productName: '',
          productCode: '',
          service: '',
          serviceName: '',
          serviceCode: '',
          category: '',
          categoryName: '',
          description: '',
          quantity: 1,
          unitOfMeasurement: 'Unit',
          unitPrice: 0,
          discount: 0,
          discountAmount: 0,
          taxRate: 0,
          taxAmount: 0,
          totalAmount: 0,
          currency: 'INR',
          expectedDeliveryDate: ''
        }
      ]
    }))
  }

  const removeOrderItem = (index) => {
    setFormData(prev => ({
      ...prev,
      orderItems: prev.orderItems.filter((_, i) => i !== index).map((item, idx) => ({
        ...item,
        itemNumber: idx + 1
      }))
    }))
    calculateTotals()
  }

  // Recalculate totals when items or charges change
  useEffect(() => {
    if (formData.orderItems.length > 0) {
      const subtotal = formData.orderItems.reduce((sum, item) => 
        sum + ((item.unitPrice || 0) * (item.quantity || 0)), 0)
      const totalDiscount = formData.orderItems.reduce((sum, item) => 
        sum + (item.discountAmount || 0), 0)
      const totalTaxAmount = formData.orderItems.reduce((sum, item) => 
        sum + (item.taxAmount || 0), 0)
      const totalAmount = subtotal - totalDiscount + totalTaxAmount + 
        (formData.financialSummary.shippingCharges || 0) + 
        (formData.financialSummary.otherCharges || 0)

      setFormData(prev => ({
        ...prev,
        financialSummary: {
          ...prev.financialSummary,
          subtotal,
          totalDiscount,
          totalTaxAmount,
          totalAmount
        }
      }))
    }
  }, [formData.orderItems, formData.financialSummary.shippingCharges, formData.financialSummary.otherCharges])

  // Recalculate advance amount when total amount or advance percentage changes
  useEffect(() => {
    const advanceAmount = (formData.financialSummary.totalAmount * (formData.paymentTerms.advancePercentage || 0)) / 100
    setFormData(prev => ({
      ...prev,
      paymentTerms: {
        ...prev.paymentTerms,
        advanceAmount
      }
    }))
  }, [formData.financialSummary.totalAmount, formData.paymentTerms.advancePercentage])

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.supplier || !formData.expectedDeliveryDate) {
        toast.error('Please fill in all required fields (Supplier and Expected Delivery Date)')
        return
      }

      if (!formData.orderItems || formData.orderItems.length === 0) {
        toast.error('Please add at least one order item')
        return
      }

      setLoading(true)

      // Recalculate totals before saving
      const totals = calculateTotals()
      const submitData = {
        ...formData,
        financialSummary: {
          ...formData.financialSummary,
          ...totals
        }
      }

      if (isEdit && orderData?._id) {
        const response = await purchaseOrderService.updatePurchaseOrder(orderData._id, submitData)
        if (response.success) {
          toast.success('Purchase order updated successfully!')
          navigate('/procurement/purchase-order')
        } else {
          toast.error(response.message || 'Failed to update purchase order')
        }
      } else {
        const response = await purchaseOrderService.createPurchaseOrder(submitData)
        if (response.success) {
          toast.success('Purchase order created successfully!')
          navigate('/procurement/purchase-order')
        } else {
          toast.error(response.message || 'Failed to create purchase order')
        }
      }
    } catch (error) {
      console.error('Error saving purchase order:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save purchase order')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSend = async () => {
    if (!orderData?._id) {
      // Save first, then send
      await handleSave()
      return
    }

    try {
      setLoading(true)
      const response = await purchaseOrderService.sendPurchaseOrder(orderData._id)
      if (response.success) {
        toast.success('Purchase order sent successfully!')
        navigate('/procurement/purchase-order')
      } else {
        toast.error(response.message || 'Failed to send purchase order')
      }
    } catch (error) {
      console.error('Error sending purchase order:', error)
      if (!error._errorHandled) {
        toast.error('Failed to send purchase order')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    if (isEdit || isView) {
      loadOrderData(orderData._id)
    } else {
      setFormData(prev => ({
        ...prev,
        purchaseRequisition: '',
        requisitionNumber: '',
        supplier: '',
        supplierName: '',
        supplierCode: '',
        supplierContact: {
          contactPerson: '',
          email: '',
          phone: ''
        },
        supplierAddress: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'India'
        },
        expectedDeliveryDate: '',
        deliveryAddress: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'India'
        },
        orderItems: [],
        financialSummary: {
          subtotal: 0,
          totalDiscount: 0,
          totalTaxAmount: 0,
          shippingCharges: 0,
          otherCharges: 0,
          totalAmount: 0,
          currency: 'INR'
        },
        paymentTerms: {
          paymentMethod: 'Net 30',
          paymentTermsDescription: '',
          advancePercentage: 0,
          advanceAmount: 0
        },
        deliveryTerms: {
          deliveryMethod: 'Supplier Delivery',
          incoterms: '',
          deliveryTermsDescription: ''
        },
        project: '',
        projectName: '',
        department: '',
        costCenter: '',
        notes: '',
        internalNotes: ''
      }))
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Procurement', path: '/procurement' },
    { label: 'Purchase Orders', path: '/procurement/purchase-order' },
    { 
      label: isEdit ? 'Edit Purchase Order' : isView ? 'View Purchase Order' : 'New Purchase Order', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (loading && (isEdit || isView)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading purchase order...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEdit ? 'Edit Purchase Order' : isView ? 'View Purchase Order' : 'New Purchase Order'}</h1>
          <p>{isEdit ? 'Update purchase order' : isView ? 'View purchase order details' : 'Create a new purchase order'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/procurement/purchase-order')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Order Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header"><h3>Basic Information</h3></div>
          {/* Row 1: PO Number, Requisition, Supplier, Order Date */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="purchaseOrderNumber" className="form-label required">
                PO Number
              </label>
              <input
                type="text"
                id="purchaseOrderNumber"
                name="purchaseOrderNumber"
                value={isLoadingOrderNumber ? 'Generating...' : formData.purchaseOrderNumber}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="purchaseRequisition" className="form-label">
                Purchase Requisition
              </label>
              <select
                id="purchaseRequisition"
                name="purchaseRequisition"
                value={formData.purchaseRequisition}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
              >
                <option value="">Select Requisition (Optional)</option>
                {requisitions.map(req => (
                  <option key={req._id} value={req._id}>
                    {req.requisitionNumber} - {req.requesterName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="supplier" className="form-label required">
                Supplier
              </label>
              <select
                id="supplier"
                name="supplier"
                value={formData.supplier}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                required
              >
                <option value="">Select Supplier</option>
                {suppliers.map(supplier => (
                  <option key={supplier._id} value={supplier._id}>
                    {supplier.supplierName} ({supplier.supplierCode})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="orderDate" className="form-label required">
                Order Date
              </label>
              <input
                type="date"
                id="orderDate"
                name="orderDate"
                value={formData.orderDate}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                required
              />
            </div>
          </div>

          {/* Row 2: Expected Delivery Date */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="expectedDeliveryDate" className="form-label required">
                Expected Delivery Date
              </label>
              <input
                type="date"
                id="expectedDeliveryDate"
                name="expectedDeliveryDate"
                value={formData.expectedDeliveryDate}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                required
              />
            </div>
          </div>

          {/* Order Items Section */}
          <div className="form-section-divider">
            <div className="section-header-with-action">
              <div>
                <h2 className="section-title">Order Items</h2>
                <p className="section-subtitle">Add products or services to order</p>
              </div>
              {!isView && (
                <button
                  type="button"
                  onClick={addOrderItem}
                  className="btn btn-primary"
                  style={{
                    padding: '10px 20px',
                    background: '#292752',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  <Plus size={16} />
                  Add Item
                </button>
              )}
            </div>
          </div>

          {formData.orderItems.map((item, index) => (
            <div key={index} className="expense-item-card-modern">
              <div className="expense-item-header">
                <div className="expense-item-number">
                  <span className="item-number-badge">{index + 1}</span>
                  <span className="item-number-text">Item {index + 1}</span>
                </div>
                {!isView && (
                  <button
                    type="button"
                    onClick={() => removeOrderItem(index)}
                    className="btn-icon-danger"
                    title="Remove item"
                    style={{
                      padding: '8px',
                      border: 'none',
                      background: '#dc3545',
                      color: '#fff',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              <div className="grid-row-4">
                <div className="form-group">
                  <label className="form-label required">Item Type</label>
                  <select
                    value={item.itemType || 'Product'}
                    onChange={(e) => handleItemChange(index, 'itemType', e.target.value)}
                    className="form-input"
                    disabled={isView}
                  >
                    <option value="Product">Product</option>
                    <option value="Service">Service</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label required">
                    {item.itemType === 'Product' ? 'Product' : 'Service'}
                  </label>
                  <select
                    value={item.itemType === 'Product' ? (item.product || '') : (item.service || '')}
                    onChange={(e) => handleItemChange(index, item.itemType === 'Product' ? 'product' : 'service', e.target.value)}
                    className="form-input"
                    required
                    disabled={isView}
                  >
                    <option value="">Select {item.itemType === 'Product' ? 'Product' : 'Service'}</option>
                    {item.itemType === 'Product' 
                      ? products.map(product => (
                          <option key={product._id} value={product._id}>
                            {product.productName} ({product.productCode})
                          </option>
                        ))
                      : services.map(service => (
                          <option key={service._id} value={service._id}>
                            {service.serviceName} ({service.serviceCode})
                          </option>
                        ))
                    }
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    value={item.category || ''}
                    onChange={(e) => handleItemChange(index, 'category', e.target.value)}
                    className="form-input"
                    disabled={isView}
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat._id} value={cat._id}>
                        {cat.categoryName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    value={item.description || ''}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    className="form-input"
                    placeholder="Item description"
                    disabled={isView}
                  />
                </div>
              </div>

              <div className="grid-row-4" style={{ marginTop: '10px' }}>
                <div className="form-group">
                  <label className="form-label required">Quantity</label>
                  <input
                    type="number"
                    value={item.quantity || 0}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    className="form-input"
                    min="0.01"
                    step="0.01"
                    required
                    disabled={isView}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Unit</label>
                  <input
                    type="text"
                    value={item.unitOfMeasurement || ''}
                    readOnly
                    className="form-input"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">Unit Price</label>
                  <input
                    type="number"
                    value={item.unitPrice || 0}
                    onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                    className="form-input"
                    min="0"
                    step="0.01"
                    required
                    disabled={isView}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Discount (%)</label>
                  <input
                    type="number"
                    value={item.discount || 0}
                    onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                    className="form-input"
                    min="0"
                    max="100"
                    disabled={isView}
                  />
                </div>
              </div>

              <div className="grid-row-4" style={{ marginTop: '10px' }}>
                <div className="form-group">
                  <label className="form-label">Tax Rate (%)</label>
                  <input
                    type="number"
                    value={item.taxRate || 0}
                    onChange={(e) => handleItemChange(index, 'taxRate', e.target.value)}
                    className="form-input"
                    min="0"
                    max="100"
                    disabled={isView}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Total Amount</label>
                  <div className="expense-item-total-display">
                    {item.currency || 'INR'} {(item.totalAmount || 0).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {formData.orderItems.length === 0 && !isView && (
            <div className="empty-state-card">
              <div className="empty-state-icon">
                <ShoppingCart size={48} />
              </div>
              <h3 className="empty-state-title">No order items added</h3>
              <p className="empty-state-description">Click "Add Item" to start adding products or services</p>
            </div>
          )}

          {/* Financial Summary */}
          {formData.orderItems.length > 0 && (
            <div className="expense-totals-card">
              <div className="totals-row">
                <div className="total-item">
                  <span className="total-label">Subtotal</span>
                  <span className="total-value">INR {formData.financialSummary.subtotal.toFixed(2)}</span>
                </div>
                <div className="total-item">
                  <span className="total-label">Discount</span>
                  <span className="total-value">INR {formData.financialSummary.totalDiscount.toFixed(2)}</span>
                </div>
                <div className="total-item">
                  <span className="total-label">Tax</span>
                  <span className="total-value">INR {formData.financialSummary.totalTaxAmount.toFixed(2)}</span>
                </div>
                <div className="total-item">
                  <span className="total-label">Shipping</span>
                  <input
                    type="number"
                    name="financialSummary.shippingCharges"
                    value={formData.financialSummary.shippingCharges || 0}
                    onChange={handleInputChange}
                    className="form-input"
                    style={{ width: '120px', textAlign: 'right' }}
                    min="0"
                    step="0.01"
                    disabled={isView}
                  />
                </div>
                <div className="total-item">
                  <span className="total-label">Other Charges</span>
                  <input
                    type="number"
                    name="financialSummary.otherCharges"
                    value={formData.financialSummary.otherCharges || 0}
                    onChange={handleInputChange}
                    className="form-input"
                    style={{ width: '120px', textAlign: 'right' }}
                    min="0"
                    step="0.01"
                    disabled={isView}
                  />
                </div>
                <div className="total-item grand-total">
                  <span className="total-label">Grand Total</span>
                  <span className="total-value">INR {formData.financialSummary.totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Payment Terms */}
          <div className="section-header"><h3>Payment Terms</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="paymentTerms.paymentMethod" className="form-label">
                Payment Method
              </label>
              <select
                id="paymentTerms.paymentMethod"
                name="paymentTerms.paymentMethod"
                value={formData.paymentTerms.paymentMethod}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
              >
                <option value="Cash">Cash</option>
                <option value="Credit">Credit</option>
                <option value="Net 15">Net 15</option>
                <option value="Net 30">Net 30</option>
                <option value="Net 45">Net 45</option>
                <option value="Net 60">Net 60</option>
                <option value="Advance">Advance</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="paymentTerms.advancePercentage" className="form-label">
                Advance Percentage (%)
              </label>
              <input
                type="number"
                id="paymentTerms.advancePercentage"
                name="paymentTerms.advancePercentage"
                value={formData.paymentTerms.advancePercentage}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                min="0"
                max="100"
              />
            </div>

            <div className="form-group">
              <label htmlFor="paymentTerms.advanceAmount" className="form-label">
                Advance Amount
              </label>
              <input
                type="number"
                id="paymentTerms.advanceAmount"
                name="paymentTerms.advanceAmount"
                value={formData.paymentTerms.advanceAmount}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="paymentTerms.paymentTermsDescription" className="form-label">
              Payment Terms Description
            </label>
            <textarea
              id="paymentTerms.paymentTermsDescription"
              name="paymentTerms.paymentTermsDescription"
              value={formData.paymentTerms.paymentTermsDescription}
              onChange={handleInputChange}
              disabled={isView}
              className="form-input"
              rows="2"
              placeholder="Additional payment terms..."
            />
          </div>

          {/* Delivery Terms */}
          <div className="section-header"><h3>Delivery Terms</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="deliveryTerms.deliveryMethod" className="form-label">
                Delivery Method
              </label>
              <select
                id="deliveryTerms.deliveryMethod"
                name="deliveryTerms.deliveryMethod"
                value={formData.deliveryTerms.deliveryMethod}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
              >
                <option value="Pickup">Pickup</option>
                <option value="Supplier Delivery">Supplier Delivery</option>
                <option value="Third Party Logistics">Third Party Logistics</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="deliveryTerms.incoterms" className="form-label">
                Incoterms
              </label>
              <select
                id="deliveryTerms.incoterms"
                name="deliveryTerms.incoterms"
                value={formData.deliveryTerms.incoterms}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
              >
                <option value="">Select Incoterms</option>
                <option value="EXW">EXW - Ex Works</option>
                <option value="FOB">FOB - Free On Board</option>
                <option value="CIF">CIF - Cost, Insurance and Freight</option>
                <option value="DDP">DDP - Delivered Duty Paid</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="deliveryTerms.deliveryTermsDescription" className="form-label">
              Delivery Terms Description
            </label>
            <textarea
              id="deliveryTerms.deliveryTermsDescription"
              name="deliveryTerms.deliveryTermsDescription"
              value={formData.deliveryTerms.deliveryTermsDescription}
              onChange={handleInputChange}
              disabled={isView}
              className="form-input"
              rows="2"
              placeholder="Additional delivery terms..."
            />
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isView}
              className="form-input"
              rows="3"
              placeholder="Enter any additional notes"
            />
          </div>

          <div className="form-group">
            <label htmlFor="internalNotes" className="form-label">
              Internal Notes
            </label>
            <textarea
              id="internalNotes"
              name="internalNotes"
              value={formData.internalNotes}
              onChange={handleInputChange}
              disabled={isView}
              className="form-input"
              rows="3"
              placeholder="Internal notes (not visible to supplier)"
            />
          </div>

          {/* Action Buttons */}
          {!isView && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleReset}
                className="btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="btn-primary"
                disabled={loading}
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: loading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {loading ? 'Saving...' : 'Save'}
              </button>
              {isEdit && orderData && orderData.status === 'Draft' && (
                <button
                  type="button"
                  onClick={handleSend}
                  className="btn-primary"
                  disabled={loading}
                  style={{
                    padding: '10px 24px',
                    background: '#28a745',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  <Send size={16} />
                  Send PO
                </button>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default PurchaseOrderForm

